/**
 * Shared primitives for the encrypted address-book server backup (#476).
 *
 * These wrap the GET/PUT endpoints, the AES-GCM encrypt/decrypt, and the
 * device-local sync bookkeeping so both the background sync hook
 * (useAddressBookSync) and the manual "restore from backup" settings action work
 * off one implementation.
 */

import { StorageService } from '@/services/storage';
import { encryptVcf, decryptVcf } from './addressBookCrypto';
import { apiCall } from '@/util/api';
import { contactsFromVCards } from './util';
import type { ContactList } from './types';

export const ADDRESS_BOOK_ENDPOINT = '/api/address-book';

/** Device-local bookkeeping for the server sync. */
export interface AddressBookSyncState {
  /** Last server version this device synced to (0 = never). */
  serverVersion: number;
  /** ISO timestamp of that version — the last common ancestor / divergence baseline. */
  serverVersionAt: string | null;
  /**
   * bookSignature of the VCF last pushed or pulled, to detect un-synced local
   * edits ('' = never synced). Null in bookkeeping written before signatures,
   * which carries only lastPushedHash.
   */
  lastSyncedSignature: string | null;
  /**
   * Whole-VCF content hash kept by bookkeeping written before signatures. Read
   * only while lastSyncedSignature is null; the next push or pull replaces it.
   */
  lastPushedHash?: string;
}

export const EMPTY_SYNC_STATE: AddressBookSyncState = {
  serverVersion: 0,
  serverVersionAt: null,
  lastSyncedSignature: '',
};

/** Small stable non-cryptographic hash (FNV-1a, 32-bit) for change detection. */
export function contentHash(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16);
}

/**
 * Hash of a serialized book's user content: the VCF without its X-SNOWBALL-*
 * lines. Those carry snowball enrichment (a contact's snowball id and when it
 * was last looked up), which each device's background snowball sync rewrites on
 * its own; counting it as an edit would make every device look edited and turn
 * two devices enriching at once into a conflict prompt on each.
 */
export function bookSignature(vcf: string): string {
  return contentHash(vcf.replace(/^X-SNOWBALL-[^\n]*\n?/gm, ''));
}

/** Whether the serialized book differs from what this device last synced. */
export function hasUnsyncedChanges(
  sync: AddressBookSyncState,
  vcf: string
): boolean {
  if (sync.lastSyncedSignature !== null) {
    return bookSignature(vcf) !== sync.lastSyncedSignature;
  }
  return contentHash(vcf) !== (sync.lastPushedHash ?? '');
}

export async function readSyncState(): Promise<AddressBookSyncState> {
  const raw = await StorageService.getAddressBookSync();
  if (!raw) return EMPTY_SYNC_STATE;
  try {
    const stored = JSON.parse(raw) as Partial<AddressBookSyncState>;
    return {
      serverVersion: stored.serverVersion ?? 0,
      serverVersionAt: stored.serverVersionAt ?? null,
      lastSyncedSignature:
        typeof stored.lastSyncedSignature === 'string'
          ? stored.lastSyncedSignature
          : null,
      ...(typeof stored.lastSyncedSignature !== 'string' && {
        lastPushedHash: stored.lastPushedHash ?? '',
      }),
    };
  } catch {
    return EMPTY_SYNC_STATE;
  }
}

export async function writeSyncState(
  state: AddressBookSyncState
): Promise<void> {
  await StorageService.setAddressBookSync(JSON.stringify(state));
}

/**
 * Mark the device so the next sync restores the address book from the server
 * backup instead of pushing the now-cleared local book up. Used when the user
 * clears their local address book during a reset but keeps the server backup:
 * resetting the last-synced version to 0 makes any existing server copy look
 * newer, and recording the empty book's signature (it serializes to "") means
 * the empty local state is not treated as an un-synced edit — so the reconciler
 * pulls rather than pushes.
 */
export async function markAddressBookForRestore(): Promise<void> {
  await writeSyncState({
    serverVersion: 0,
    serverVersionAt: null,
    lastSyncedSignature: bookSignature(''),
  });
}

/** The server's current backup metadata + ciphertext, as returned by GET. */
export interface ServerBackup {
  exists: boolean;
  version: number;
  ciphertext?: string;
  nonce?: string;
  updatedAt?: string;
  /**
   * The device id that wrote this version, as recorded by the server. Lets a
   * client recognise its own push — including one whose response it never
   * received — instead of mistaking it for another device's write.
   */
  lastDeviceId?: string;
}

/**
 * Fetch the server backup. Returns null on a transport error or non-ok response
 * (caller should retry later); a resolved value with `exists: false` means the
 * server simply holds no copy yet.
 */
export async function fetchServerBackup(): Promise<ServerBackup | null> {
  let resp: Response;
  try {
    resp = await apiCall(ADDRESS_BOOK_ENDPOINT);
  } catch {
    return null;
  }
  if (!resp.ok) return null;
  const j = (await resp.json()) as ServerBackup;
  return {
    exists: j.exists ?? false,
    version: j.version ?? 0,
    ciphertext: j.ciphertext,
    nonce: j.nonce,
    updatedAt: j.updatedAt,
    lastDeviceId: j.lastDeviceId,
  };
}

/** Decrypt a fetched backup into contacts. Throws if it can't be decrypted. */
export async function decryptBackupToContacts(
  phone: string,
  backup: ServerBackup
): Promise<ContactList> {
  const vcf = await decryptVcf(phone, {
    ciphertext: backup.ciphertext ?? '',
    nonce: backup.nonce ?? '',
  });
  return contactsFromVCards(vcf);
}

/** Outcome of pushing the local book to the server. */
export type PushOutcome =
  | { status: 'ok'; version: number; updatedAt: string | null }
  | {
      status: 'conflict';
      /** The server's live version, from the 409 body (0 if unreadable). */
      version: number;
      updatedAt: string | null;
      /** The device that wrote that version — ours means the conflict is ours. */
      lastDeviceId: string | null;
    }
  | { status: 'error' };

/** Encrypt and PUT the local book with optimistic concurrency. */
export async function pushBackup(
  phone: string,
  vcf: string,
  expectedVersion: number
): Promise<PushOutcome> {
  const blob = await encryptVcf(phone, vcf);
  let resp: Response;
  try {
    resp = await apiCall(ADDRESS_BOOK_ENDPOINT, {
      method: 'PUT',
      body: JSON.stringify({
        expectedVersion,
        ciphertext: blob.ciphertext,
        nonce: blob.nonce,
      }),
    });
  } catch {
    return { status: 'error' };
  }
  if (resp.status === 409) {
    // The 409 body carries the live version and its writer. Treat an unreadable
    // body as an unattributed conflict rather than failing the whole push.
    let body: { version?: number; updatedAt?: string; lastDeviceId?: string } =
      {};
    try {
      body = await resp.json();
    } catch {
      body = {};
    }
    return {
      status: 'conflict',
      version: body.version ?? 0,
      updatedAt: body.updatedAt ?? null,
      lastDeviceId: body.lastDeviceId ?? null,
    };
  }
  if (!resp.ok) return { status: 'error' };
  const j = (await resp.json()) as { version?: number; updatedAt?: string };
  return {
    status: 'ok',
    version: j.version ?? expectedVersion + 1,
    updatedAt: j.updatedAt ?? null,
  };
}

/**
 * Human-readable age of the server's backup, for the restore confirmation
 * ("The server's copy is 5 minutes old"). Rounds down to the coarsest unit.
 */
export function formatBackupAge(
  updatedAt: string | undefined,
  now: Date = new Date()
): string {
  if (!updatedAt) return 'of unknown age';
  const then = new Date(updatedAt).getTime();
  if (Number.isNaN(then)) return 'of unknown age';

  const diffMs = Math.max(0, now.getTime() - then);
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'less than a minute old';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} old`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} old`;

  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} old`;
}
